import React, { memo, FC, useEffect, useState, useCallback } from 'react'

import { useFocusHierarchy, useFocusNodeById, useSetFocus } from '@please/lrud'
import { TCFUserDecisions, UserDecision } from '@usercentrics/cmp-browser-sdk'

import { getPrivacyRegulation } from '@/actions'
import { fetchStatus } from '@/constants/common'
import { focusIds, orientation } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { scrollOptions } from '@/constants/scrollers'
import { ScrollContainer } from '@/containers/styles'
import { useCanShowUcPopup } from '@/containers/Usercentrics/hooks'
import useStore from '@/store/useStore'
import { Labels } from '@/types/componentsPropsTypes'

import { PrivacySettings } from './PrivacySettings'
import {
  UsercentricsBox,
  Title,
  Description,
  UsercentricsContainer,
  HeaderLogoEl,
  UsercentricsScreenscontent,
} from './styles'
import { UsercentricsActions } from './UsercentricsActions'
import {
  consentShown,
  getSettingsLabels,
  getUCServicesDecisions,
  getUCTcfUserDecisions,
} from './usercentricsUtils'

export enum UsercentricsLayers {
  FIRST_LAYER = 'firstLayer',
  SECOND_LAYER = 'secondLayer',
}

const focusId = `${focusIds.USERCENTRICS}_${focusIds.POPUP}`

const UsercentricsRaw: FC = () => {
  const usercentricsPopup = useStore((state) => state.common.usercentricsPopup)
  const setConsentStatus = useStore((state) => state.common.setConsentStatus)
  const [activeLayer, setActiveLayer] = useState(UsercentricsLayers.FIRST_LAYER)
  const [defaultLabels, setDefaultLabels] = useState<Partial<Labels>>()
  const [servicesDecision, setServicesDecision] = useState<Array<UserDecision>>(
    [],
  )
  const [tcfUserDecisions, setTcfUserDecisions] = useState<TCFUserDecisions>({})
  const [privacyRegulation, setPrivacyRegulation] = useState('')
  const setFocus = useSetFocus()
  const focusNode = useFocusNodeById(focusId)
  const hasDefaultLabels = !!defaultLabels
  const canShowUcPopup = useCanShowUcPopup(hasDefaultLabels)

  useEffect(() => {
    if (!usercentricsPopup) {
      return
    }
    const privacyRegulation = getPrivacyRegulation()
    setPrivacyRegulation(privacyRegulation)

    getUCLabels()
    getServicesDecision()
    getTcfUserDecisions()

    return () => {
      setConsentStatus(fetchStatus.COMPLETED)
      setActiveLayer(UsercentricsLayers.FIRST_LAYER)
      setDefaultLabels(undefined)
      setServicesDecision([])
      setTcfUserDecisions({})
      setFocus(focusIds.CONTENT)
    }
  }, [usercentricsPopup])

  useEffect(() => {
    canShowUcPopup && consentShown()
  }, [canShowUcPopup])

  const focusHierarchy = useFocusHierarchy()

  useEffect(() => {
    const isUsercentricsPopupFocused = focusHierarchy.some((node) =>
      node.focusId.includes(focusId),
    )
    if (canShowUcPopup && focusNode && !isUsercentricsPopupFocused) {
      setTimeout(setFocus, 0, focusId)
    }
  }, [canShowUcPopup, focusNode, focusHierarchy])

  const getUCLabels = useCallback(() => {
    try {
      const defaultLabels = getSettingsLabels()
      setDefaultLabels(defaultLabels)
    } catch {
      console.error('[Usercentrics] init error')
    }
  }, [setDefaultLabels])

  const getServicesDecision = useCallback(() => {
    const servicesDecision = getUCServicesDecisions()
    setServicesDecision(servicesDecision || [])
  }, [setServicesDecision])

  const getTcfUserDecisions = useCallback(() => {
    const tcfUserDecisions = getUCTcfUserDecisions()
    setTcfUserDecisions(tcfUserDecisions)
  }, [setTcfUserDecisions])

  if (!canShowUcPopup || !hasDefaultLabels) {
    return null
  }

  return (
    <UsercentricsContainer
      focusId={focusId}
      orientation={orientation.VERTICAL}
      isTrap={true}
    >
      <UsercentricsBox>
        <HeaderLogoEl
          src={icons.logoApp.logoWhite}
          alt="logo"
          className="header-logo"
        />
        {activeLayer === UsercentricsLayers.FIRST_LAYER && (
          <>
            <UsercentricsScreenscontent>
              <Title>{defaultLabels[activeLayer]?.title}</Title>
              <Description>
                {defaultLabels[activeLayer]?.description.default || ''}
                <br />
                {defaultLabels[activeLayer]?.description.resurfaceNote || ''}
              </Description>
            </UsercentricsScreenscontent>
            {defaultLabels.buttons && (
              <UsercentricsActions
                activeLayer={activeLayer}
                buttonsLabels={defaultLabels.buttons}
                servicesDecision={servicesDecision}
                tcfUserDecisions={tcfUserDecisions}
                setActiveLayer={() =>
                  setActiveLayer(UsercentricsLayers.SECOND_LAYER)
                }
              />
            )}
          </>
        )}
        {activeLayer === UsercentricsLayers.SECOND_LAYER && (
          <>
            <ScrollContainer
              id={scrollOptions.UsercentricsScroll.containerId}
              height={scrollOptions.UsercentricsScroll.height}
            >
              <Title>{defaultLabels[activeLayer]?.title}</Title>
              <Description>
                {defaultLabels[activeLayer]?.description}
              </Description>
              <PrivacySettings
                defaultLabels={defaultLabels.secondLayer}
                servicesDecision={servicesDecision}
                setServicesDecision={setServicesDecision}
                tcfUserDecisions={tcfUserDecisions}
                setTcfUserDecisions={setTcfUserDecisions}
                privacyRegulation={privacyRegulation}
              />
            </ScrollContainer>
            {defaultLabels.buttons && (
              <UsercentricsActions
                activeLayer={activeLayer}
                buttonsLabels={defaultLabels.buttons}
                servicesDecision={servicesDecision}
                tcfUserDecisions={tcfUserDecisions}
              />
            )}
          </>
        )}
      </UsercentricsBox>
    </UsercentricsContainer>
  )
}

export const UsercentricsPopup = memo(UsercentricsRaw)
